import "./login.scss";
import { auth, db } from "../../firebase";
import {
  Button,
  CssBaseline,
  TextField,
  Box,
  Typography,
  Container,
  Paper,
  Stack,
  IconButton,
  InputAdornment,
  Fade,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  useTheme,
  alpha,
  Divider,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  ArrowForward,
} from "@mui/icons-material";
import { useUserAuth } from "../../context/AuthContext";
import { Navigate } from "react-router-dom";
import { onAuthStateChanged, sendPasswordResetEmail } from "firebase/auth";
import SpinnerLoading from "../../components/spinner/SpinnerLoading";
import { useEffect, useState } from "react";
import { isValidEmail } from "../../context/commonFunctions";
import { doc, getDoc } from "firebase/firestore";

function Copyright(props) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      sx={{ opacity: 0.7 }}
      {...props}
    >
      {"© "}
      <Link
        color="inherit"
        href="https://wishallbook.com/"
        sx={{
          textDecoration: "none",
          "&:hover": { textDecoration: "underline" },
        }}
      >
        WishallBook
      </Link>{" "}
      {new Date().getFullYear()}
    </Typography>
  );
}

const Login = () => {
  const [email, setEmail] = useState("");
  const [emailReset, setEmailReset] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorReset, setErrorReset] = useState("");
  const [errorMessage, setErrorMessage] = useState({
    email: "",
    password: "",
  });
  const [isLoading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const { logIn, logOut } = useUserAuth();
  const [user, setUser] = useState(null);
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  const handleClickOpen = () => {
    setErrorReset("");
    setEmailReset("");
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    document.title = "Login to WishallBook Publisher Dashboard";
  }, []);
  const mapStrings = (errorCode) => {
    switch (errorCode) {
      case "auth/invalid-email":
        return "Please enter a valid email address.";
      case "auth/missing-password":
        return "Please enter a password.";
      case "auth/user-not-found":
        return "Invalid credentials";
      case "auth/wrong-password":
        return "The password entered is incorrect.";
      // Add more cases as needed
      default:
        return "Something went wrong. Please try again."; // Return the original string if no match
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setErrorMessage({
      email: "",
      password: "",
    });

    if (!email.trim()) {
      setErrorMessage({
        ...errorMessage,
        email: "Please enter email address",
      });
    } else if (!isValidEmail(email)) {
      setErrorMessage({
        ...errorMessage,
        email: "Please enter a valid email address",
      });
    } else if (!password.trim()) {
      setErrorMessage({
        ...errorMessage,
        password: "Password should not be empty",
      });
    } else {
      setLoading(true);

      try {
        await logIn(email, password);
      } catch (err) {
        const errorCode = err.code;
        const errorMessage = mapStrings(errorCode);
        alert(errorMessage);
        setLoading(false);
      }
    }
  };
  const handlePasswordInput = (e) => {
    setErrorMessage({
      ...errorMessage,
      password: "",
    });
    setPassword(e.target.value);
  };
  const handleEmailInput = (e) => {
    setErrorMessage({
      ...errorMessage,
      email: "",
    });
    setEmail(e.target.value);
  };
  const handleEmailResetInput = (e) => {
    setErrorReset("");
    setEmailReset(e.target.value);
  };

  useEffect(() => {
    const unsubsribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const userUid = currentUser.uid;

        const publisgerDocRef = doc(db, "publishers", userUid);
        try {
          const pubDocSnapshot = await getDoc(publisgerDocRef);
          if (pubDocSnapshot.exists()) {
            setUser(currentUser);
            localStorage.setItem("publisherID", currentUser.uid);
            localStorage.setItem("isPubLoggedIn", true);
          } else {
            setLoading(false);
            await logOut();
            alert("Publisher doesn't exists. Please contact support for help.");
          }
        } catch (error) {
          console.error("Error checking user document:", error);
        }
      } else {
        setUser(null);
        localStorage.removeItem("publisherID");
        localStorage.removeItem("isPubLoggedIn");
      }
      setInitialLoading(false);
    });

    return () => {
      unsubsribe();
    };
  }, []);

  if (user) {
    return <Navigate to="/" />;
  }

  return (
    <>
      <CssBaseline />
      {initialLoading && <SpinnerLoading />}
      {!initialLoading && !user && (
        <Box className="login-container">
          {/* Background Elements */}
          <Box className="background-elements">
            <Box className="floating-shape shape-1" />
            <Box className="floating-shape shape-2" />
            <Box className="floating-shape shape-3" />
          </Box>

          <Container component="main" maxWidth="sm">
            <Fade in={!initialLoading} timeout={800}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, sm: 5 },
                  borderRadius: 4,
                  background: alpha(theme.palette.background.paper, 0.8),
                  backdropFilter: "blur(20px)",
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  boxShadow: `0 8px 32px ${alpha(
                    theme.palette.common.black,
                    0.08
                  )}`,
                }}
              >
                <Stack spacing={4} alignItems="center">
                  {/* Logo and Header */}
                  <Stack spacing={2} alignItems="center" textAlign="center">
                    <img
                      src="/image.png"
                      alt="Wishallbook Logo"
                      style={{
                        height: 30,
                        marginRight: 8,
                      }}
                    />

                    <Stack spacing={1}>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ opacity: 0.8 }}
                      >
                        Sign in to your publisher dashboard
                      </Typography>
                    </Stack>
                  </Stack>

                  {/* Login Form */}
                  <Box
                    component="form"
                    onSubmit={handleSubmit}
                    noValidate
                    sx={{ width: "100%", p: 4, maxWidth: 400 }}
                  >
                    <Stack spacing={3}>
                      <TextField
                        error={errorMessage.email.length > 0}
                        fullWidth
                        id="email"
                        label="Email address"
                        name="email"
                        autoComplete="email"
                        value={email}
                        onChange={handleEmailInput}
                        autoFocus
                        helperText={errorMessage.email}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Email
                                sx={{ color: "text.secondary", fontSize: 20 }}
                              />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            backgroundColor: alpha(
                              theme.palette.background.paper,
                              0.5
                            ),
                            "&:hover": {
                              backgroundColor: alpha(
                                theme.palette.background.paper,
                                0.8
                              ),
                            },
                            "&.Mui-focused": {
                              backgroundColor: theme.palette.background.paper,
                            },
                          },
                        }}
                      />

                      <TextField
                        error={errorMessage.password.length > 0}
                        fullWidth
                        name="password"
                        label="Password"
                        type={showPassword ? "text" : "password"}
                        id="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={handlePasswordInput}
                        helperText={errorMessage.password}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock
                                sx={{ color: "text.secondary", fontSize: 20 }}
                              />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                                size="small"
                              >
                                {showPassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            backgroundColor: alpha(
                              theme.palette.background.paper,
                              0.5
                            ),
                            "&:hover": {
                              backgroundColor: alpha(
                                theme.palette.background.paper,
                                0.8
                              ),
                            },
                            "&.Mui-focused": {
                              backgroundColor: theme.palette.background.paper,
                            },
                          },
                        }}
                      />

                      <Box sx={{ textAlign: "right" }}>
                        <Link
                          component="button"
                          type="button"
                          variant="body2"
                          onClick={handleClickOpen}
                          sx={{
                            textDecoration: "none",
                            color: "text.secondary",
                            "&:hover": {
                              color: "primary.main",
                              textDecoration: "underline",
                            },
                          }}
                        >
                          Forgot password?
                        </Link>
                      </Box>

                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={isLoading}
                        endIcon={!isLoading && <ArrowForward />}
                        sx={{
                          py: 1.5,
                          borderRadius: 2,
                          textTransform: "none",
                          fontSize: "1rem",
                          fontWeight: 500,
                          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                          boxShadow: `0 4px 16px ${alpha(
                            theme.palette.primary.main,
                            0.3
                          )}`,
                          "&:hover": {
                            boxShadow: `0 6px 20px ${alpha(
                              theme.palette.primary.main,
                              0.4
                            )}`,
                            transform: "translateY(-1px)",
                          },
                          "&:disabled": {
                            background: alpha(
                              theme.palette.action.disabled,
                              0.12
                            ),
                            color: theme.palette.action.disabled,
                          },
                        }}
                      >
                        {isLoading ? "Signing in..." : "Sign in"}
                      </Button>
                    </Stack>
                  </Box>

                  <Divider sx={{ width: "100%", opacity: 0.5 }} />

                  {/* Author Onboarding Section */}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                    sx={{ opacity: 0.8 }}
                  >
                    Author ? Write & Earn on WishallBook |{" "}
                    <Link
                      href="https://docs.google.com/forms/d/e/1FAIpQLScO2JmieU6oZR-QrxXT0NXlLkh50lveqRgs8dfQCHRzyxK3gw/viewform"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: "primary.main",
                        textDecoration: "none",
                        fontWeight: 500,
                        "&:hover": {
                          textDecoration: "underline",
                          color: "primary.dark",
                        },
                      }}
                    >
                      Onboard Now
                    </Link>
                  </Typography>

                  <Copyright />
                </Stack>
              </Paper>
            </Fade>
          </Container>
        </Box>
      )}

      {/* Password Reset Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            Reset Password
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enter your email address and we'll send you a link to reset your
            password.
          </Typography>

          <TextField
            error={errorReset.length > 0}
            fullWidth
            id="reset-email"
            label="Email address"
            name="email"
            autoComplete="email"
            value={emailReset}
            onChange={handleEmailResetInput}
            variant="outlined"
            helperText={errorReset}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email sx={{ color: "text.secondary", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={handleClose} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              setErrorReset("");
              if (emailReset.trim() && isValidEmail(emailReset)) {
                try {
                  await sendPasswordResetEmail(auth, emailReset);
                  setOpen(false);
                  alert("Password reset link sent successfully.");
                } catch (error) {
                  setErrorReset(mapStrings(error.code));
                }
              } else {
                setErrorReset("Please enter a valid email address.");
              }
            }}
            sx={{
              textTransform: "none",
              borderRadius: 2,
            }}
          >
            Send Reset Link
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Login;
